import { useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Text,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { useQueryClient } from '@tanstack/react-query';

import {
  CognitoUser,
  ArrowLeftOutline,
  EyeOutline,
  EyeSlashOutline,
} from '@aslanpay/portal-common-lib';

import useSignIn from 'app/hooks/useSignIn';

import { authenticationSchema } from 'app/utils/schemas';

import { useAuthenticationStore } from 'app/store';

import { AuthenticationStepProps, LoginFormFields } from '../types';

function Login({ onSetStep }: AuthenticationStepProps) {
  const queryClient = useQueryClient();

  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);
  const resetAuthenticationData = useAuthenticationStore((state) => state.resetAuthenticationData);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<LoginFormFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.login),
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { mutate: signIn, isLoading: isSigningIn } = useSignIn({
    onSuccess: (data: CognitoUser) => {
      if (data.challengeName === 'SMS_MFA') {
        setAuthenticationData({ ...authenticationData, cognitoUser: data });
        onSetStep('confirmLogin');
      } else {
        queryClient.invalidateQueries({ queryKey: ['getAuthenticatedUser'] });
        resetAuthenticationData();
      }
    },
    onError: (error: Error) => {
      let message;

      switch (error.name) {
        case 'NotAuthorizedException':
          message = 'Wrong password. Please try again.';
          break;
        case 'MFAMethodNotFoundException':
          message = 'Please contact your admin to enable MFA on your account.';
          break;
        case 'PasswordResetRequiredException':
          message = 'Password reset is required for your account.';
          break;
        default:
          message = error.message;
          break;
      }

      setError('password', { message });
    },
  });

  const handleLogin = (data: LoginFormFields) => {
    setAuthenticationData({ ...authenticationData, password: data.password });
    signIn({ email: authenticationData.email, password: data.password });
  };

  return (
    <Flex flexDir="column" mt="35px">
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => onSetStep('checkEmail')}
      >
        Back
      </Button>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px" mb="35px">
        Log in
      </Text>
      <form onSubmit={handleSubmit(handleLogin)} noValidate>
        <FormControl isInvalid={!!errors?.password?.message}>
          <Flex>
            <FormLabel>Password</FormLabel>
            <Spacer />
            <Button variant="link" size="xs" onClick={() => onSetStep('resetPassword')}>
              Forgot password?
            </Button>
          </Flex>
          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              {...register('password')}
              autoFocus
            />
            <InputRightElement
              w="56px"
              h="full"
              cursor="pointer"
              onClick={() => setShowPassword((show) => !show)}
            >
              {showPassword ? (
                <EyeOutline color="brand.mutedPurple" boxSize="24px" />
              ) : (
                <EyeSlashOutline color="brand.mutedPurple" boxSize="24px" />
              )}
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
        </FormControl>
        <Button
          w="full"
          mt="35px"
          size="lg"
          type="submit"
          isLoading={isSigningIn}
          isDisabled={!isValid}
        >
          Log in
        </Button>
      </form>
    </Flex>
  );
}

export default Login;
